import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Button,
  Grid,
  Popover,
  Typography,
  TextField,
  Chip,
  Tooltip,
  CircularProgress,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Stack,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Restore as RestoreIcon,
  Refresh,
  Print,
  Download,
  Person,
  CalendarToday,
  Description,
  RemoveCircleOutline as BlankIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { blankLRList, restoreBlankLR } from "../../../Services/Services";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { T } from "../../../theme";

// ── Reusable Style Fragments ───────────────────────────────────────
const sq = { borderRadius: "4px !important" };

// ── Helpers ────────────────────────────────────────────────────────
const formatDateTime = (d) =>
  d
    ? new Date(d).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

// ── Shared Button Styles ───────────────────────────────────────────
const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.white,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};

const iconBtn = { ...ghostBtn, minWidth: 0, px: 1 };

const restoreBtn = {
  ...sq,
  border: `1.5px solid #a7f3d0`,
  bgcolor: "#ecfdf5",
  color: "#059669",
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#d1fae5", borderColor: "#059669", boxShadow: "none" },
};

// ── ActionButton ───────────────────────────────────────────────────
const ActionButton = ({ tip, icon, onClick, isRestore = false }) => {
  const color = isRestore ? "#059669" : T.primary;
  const bg = isRestore ? "#ecfdf5" : T.primaryBg;
  const border = isRestore ? "#a7f3d0" : T.border;
  const hoverBg = isRestore ? "#d1fae5" : T.primaryLight;

  return (
    <Tooltip title={tip} arrow placement="top">
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          width: 20,
          height: 20,
          ...sq,
          color,
          bgcolor: bg,
          border: `1px solid ${border}`,
          transition: "all 0.18s",
          "&:hover": {
            bgcolor: hoverBg,
            borderColor: color,
            transform: "translateY(-1px)",
          },
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 12 } })}
      </IconButton>
    </Tooltip>
  );
};

// // ── InfoRow ────────────────────────────────────────────────────────
// const InfoRow = ({ Icon, label, val }) => (
//   <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
//     <Box
//       sx={{
//         width: 24,
//         height: 24,
//         ...sq,
//         bgcolor: T.white,
//         border: `1px solid ${T.border}`,
//         display: "flex",
//         alignItems: "center",
//         justifyContent: "center",
//         flexShrink: 0,
//       }}
//     >
//       <Icon sx={{ fontSize: 13, color: "#f59e0b" }} />
//     </Box>
//     <Box>
//       <Typography
//         sx={{
//           fontSize: "0.62rem",
//           color: T.textMuted,
//           fontWeight: 600,
//           letterSpacing: "0.04em",
//           textTransform: "uppercase",
//         }}
//       >
//         {label}
//       </Typography>
//       <Typography
//         sx={{
//           fontSize: "0.75rem",
//           color: T.text,
//           fontWeight: 500,
//           lineHeight: 1.3,
//         }}
//       >
//         {val || "—"}
//       </Typography>
//     </Box>
//   </Box>
// );

// ── Mobile Card ────────────────────────────────────────────────────
const MobileBlankCard = ({ item, onRestore }) => (
  <Paper
    elevation={0}
    sx={{
      mb: 1,
      ...sq,
      border: `1.5px solid #fbbf24`,
      bgcolor: "#fffbeb",
      overflow: "hidden",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "stretch", minHeight: 64 }}>
      {/* LR Number Block */}
      <Box
        sx={{
          width: 90,
          bgcolor: "#f59e0b",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRight: `1.5px solid #d97706`,
          flexShrink: 0,
          px: 1,
        }}
      >
        <BlankIcon sx={{ fontSize: 18, color: "#fff", mb: 0.3 }} />
        <Typography
          sx={{
            fontSize: "0.95rem",
            fontWeight: 800,
            color: "#fff",
            lineHeight: 1,
          }}
        >
          {item.lr_number}
        </Typography>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          px: 1.5,
          py: 1,
          gap: 0.4,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.78rem",
            color: "#78350f",
            lineHeight: 1.2,
          }}
        >
          {item.reason || "No reason provided"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "left", gap: 0.4 }}>
          <Person sx={{ fontSize: 11, color: "#92400e" }} />
          <Typography
            sx={{
              fontSize: "0.68rem",
              color: "#92400e",
              fontWeight: 600,
            }}
          >
            {item.marked_by}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
          <CalendarToday sx={{ fontSize: 10, color: "#92400e" }} />
          <Typography
            sx={{
              fontSize: "0.65rem",
              color: "#92400e",
              fontWeight: 500,
            }}
          >
            {formatDateTime(item.marked_at)}
          </Typography>
        </Box>
      </Box>

      {/* Restore Button */}
      {/* <Box sx={{ display: "flex", alignItems: "center", pr: 1.5 }}>
        <Button
          size="small"
          onClick={(e) => onRestore(e, item)}
          startIcon={<RestoreIcon sx={{ fontSize: 12 }} />}
          sx={{
            ...restoreBtn,
            height: 28,
            fontSize: "0.62rem",
            px: 1,
            "& .MuiButton-startIcon": { mr: "3px", ml: 0 },
          }}
        >
          Restore
        </Button>
      </Box> */}
    </Box>
  </Paper>
);

// ── Desktop Table ──────────────────────────────────────────────────
const DesktopBlankTable = ({
  data,
  loading,
  totalCount,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onRestore,
}) => {
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 10,
          gap: 2,
        }}
      >
        <CircularProgress sx={{ color: "#f59e0b" }} size={36} />
        <Typography sx={{ fontSize: "0.75rem", color: T.textSub, fontWeight: 500 }}>
          Loading blank LR records...
        </Typography>
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        ...sq,
        border: `1.5px solid ${T.border}`,
        overflow: "hidden",
        boxShadow: "0 1px 8px rgba(245,158,11,0.08)",
      }}
    >
      <TableContainer sx={{ maxHeight: "calc(100vh - 280px)", overflow: "auto" }}>
        <Table
          stickyHeader
          size="small"
          sx={{
            minWidth: 800,
            borderCollapse: "collapse",
            "& td, & th": {
              py: 0,
              px: 1,
              fontSize: "0.7rem",
              borderBottom: `1px solid #ccc`,
              lineHeight: 1.2,
            },
            "& th": {
              fontWeight: 700,
              color: T.headerText,
              bgcolor: `${T.headerBg} !important`,
              py: 0.2,
              fontSize: "0.8rem",
              borderBottom: `1px solid #ccc`,
              lineHeight: 1.2,
              height: 28,
            },
            "& td": { height: 26 },
          }}
        >
          <TableHead>
            <TableRow>
              {[
                { label: "#", align: "center", sticky: true, left: 0, minW: 48 },
                { label: "LR No.", align: "center", sticky: true, left: 48, minW: 120 },
                { label: "Reason", align: "left", minW: 240 },
                { label: "Marked By", align: "center", minW: 140 },
                { label: "Marked Date", align: "center", minW: 160 },
                // { label: "Action", align: "center", sticky: true, right: 0, minW: 100 },
              ].map(({ label, align, sticky, left, right, minW }) => (
                <TableCell
                  key={label}
                  align={align}
                  sx={{
                    minWidth: minW,
                    ...(sticky && {
                      position: "sticky",
                      ...(left !== undefined && { left }),
                      ...(right !== undefined && { right }),
                      zIndex: 12,
                      bgcolor: `${T.headerBg} !important`,
                      color: `${T.headerText} !important`,
                    }),
                  }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: "center", py: 6, border: "none" }}>
                  <BlankIcon sx={{ fontSize: 40, color: T.border, mb: 1, display: "block", mx: "auto" }} />
                  <Typography sx={{ color: T.textSub, fontSize: "0.8rem", fontWeight: 600 }}>
                    No blank LR records found
                  </Typography>
                  <Typography sx={{ color: T.textMuted, fontSize: "0.7rem", mt: 0.5 }}>
                    All LR numbers are in active use
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((item, idx) => {
                const isEven = idx % 2 === 0;
                const rowBg = isEven ? T.baseRow : T.altRow;

                return (
                  <TableRow
                    key={item.blank_id}
                    sx={{
                      "& > td": {
                        bgcolor: rowBg,
                        transition: "background-color 0.12s",
                      },
                      "&:hover > td": {
                        bgcolor: `${T.rowHover} !important`,
                      },
                    }}
                  >
                    {/* S.No */}
                    <TableCell
                      align="center"
                      sx={{
                        position: "sticky",
                        left: 0,
                        bgcolor: `${rowBg} !important`,
                        zIndex: 5,
                      }}
                    >
                      <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: T.textMuted }}>
                        {page * rowsPerPage + idx + 1}
                      </Typography>
                    </TableCell>

                    {/* LR Number */}
                    <TableCell
                      align="center"
                      sx={{
                        position: "sticky",
                        left: 48,
                        bgcolor: `${rowBg} !important`,
                        zIndex: 5,
                      }}
                    >
                      <Chip
                        icon={<BlankIcon sx={{ fontSize: "12px !important", color: "#fff !important" }} />}
                        label={item.lr_number}
                        size="small"
                        sx={{
                          ...sq,
                          bgcolor: "#f59e0b",
                          color: "#fff",
                          fontWeight: 800,
                          fontSize: "0.72rem",
                          height: 20,
                          "& .MuiChip-label": { px: 0.75 },
                          "& .MuiChip-icon": { ml: 0.5 },
                        }}
                      />
                    </TableCell>

                    {/* Reason */}
                    <TableCell align="left">
                      <Tooltip title={item.reason || ""} arrow>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            color: T.text,
                            fontWeight: 500,
                            maxWidth: 240,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontStyle: item.reason === "—" ? "italic" : "normal",
                            color: item.reason === "—" ? T.textMuted : T.text,
                          }}
                        >
                          {item.reason || "—"}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {/* Marked By */}
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                        <Person sx={{ fontSize: 12, color: T.primary }} />
                        <Typography sx={{ fontSize: "0.75rem", color: T.text, fontWeight: 600 }}>
                          {item.marked_by || "—"}
                        </Typography>
                      </Stack>
                    </TableCell>

                    {/* Marked At */}
                    <TableCell align="center">
                      <Chip
                        label={formatDateTime(item.marked_at)}
                        size="small"
                        sx={{
                          fontSize: "0.6rem",
                          height: 16,
                          ...sq,
                          bgcolor: T.white,
                          color: T.primary,
                          border: `1px solid ${T.border}`,
                          fontWeight: 700,
                          "& .MuiChip-label": { px: 0.75 },
                        }}
                      />
                    </TableCell>

                    {/* Restore Action */}
                    {/* <TableCell
                      align="center"
                      sx={{
                        position: "sticky",
                        right: 0,
                        bgcolor: `${rowBg} !important`,
                        zIndex: 5,
                      }}
                    >
                      <ActionButton
                        tip="Restore LR"
                        icon={<RestoreIcon />}
                        isRestore
                        onClick={(e) => onRestore(e, item)}
                      />
                    </TableCell> */}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalCount > 0 && (
        <Box sx={{ borderTop: `1px solid ${T.border}`, bgcolor: T.white }}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            sx={{
              "& .MuiTablePagination-toolbar": { minHeight: 44, px: 2 },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "0.7rem",
                color: T.textSub,
                fontWeight: 500,
                margin: 0,
              },
              "& .MuiTablePagination-select": {
                fontSize: "0.7rem",
                ...sq,
                border: `1px solid ${T.border}`,
                bgcolor: T.white,
                px: "5px",
              },
              "& .MuiTablePagination-actions button": { color: T.primary },
              "& .MuiTablePagination-actions button:disabled": { color: T.textMuted },
            }}
          />
        </Box>
      )}
    </Paper>
  );
};

// ── Restore Confirmation Popover ───────────────────────────────────
const RestorePopover = ({ anchorEl, onClose, onConfirm, lrNumber }) => (
  <Popover
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    transformOrigin={{ vertical: "top", horizontal: "center" }}
    PaperProps={{
      sx: {
        ...sq,
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        border: `1.5px solid #a7f3d0`,
        bgcolor: T.white,
      },
    }}
  >
    <Box sx={{ p: 2, maxWidth: 260 }}>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            ...sq,
            bgcolor: "#ecfdf5",
            border: `1.5px solid #a7f3d0`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <RestoreIcon sx={{ fontSize: 20, color: "#059669" }} />
        </Box>
      </Box>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.78rem",
          color: T.text,
          textAlign: "center",
          mb: 0.5,
        }}
      >
        Restore LR {lrNumber}?
      </Typography>
      <Typography
        sx={{
          fontSize: "0.67rem",
          color: T.textSub,
          fontWeight: 500,
          textAlign: "center",
          mb: 1.75,
        }}
      >
        LR wapas available pool me daal di jayegi.
      </Typography>
      <Box sx={{ display: "flex", gap: 0.75 }}>
        <Button fullWidth size="small" onClick={onClose} sx={{ ...ghostBtn, height: 30 }}>
          Cancel
        </Button>
        <Button fullWidth size="small" onClick={onConfirm} sx={{ ...restoreBtn, height: 30 }}>
          Restore
        </Button>
      </Box>
    </Box>
  </Popover>
);

// ── Pagination Sx ──────────────────────────────────────────────────
const paginationSx = {
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  mt: 1,
  ...sq,
  "& .MuiTablePagination-toolbar": {
    minHeight: 44,
    flexWrap: "wrap",
    gap: 0.5,
    px: 1,
  },
  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
    fontSize: "0.68rem",
    margin: 0,
    color: T.textSub,
    fontWeight: 500,
  },
  "& .MuiTablePagination-select": {
    fontSize: "0.68rem",
    ...sq,
    border: `1px solid ${T.border}`,
    bgcolor: T.white,
    px: "6px",
  },
  "& .MuiTablePagination-actions button": { color: T.primary },
};

// ── MAIN COMPONENT ─────────────────────────────────────────────────
export default function BlankLRList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [restoreAnchorEl, setRestoreAnchorEl] = useState(null);
  const [itemToRestore, setItemToRestore] = useState(null);

  // ── Fetch Data ──
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await blankLRList();
      setData(res?.results || []);
    } catch {
      toast.error("Failed to load blank LR records");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Search Filter ──
  useEffect(() => {
    const q = searchTerm.toLowerCase();
    const filtered = data.filter(
      (u) =>
        !q ||
        u.lr_number?.toString().includes(q) ||
        u.reason?.toLowerCase().includes(q) ||
        u.marked_by?.toLowerCase().includes(q)
    );
    setFilteredData(filtered);
    setTotalCount(filtered.length);
    setPage(0);
  }, [data, searchTerm]);

  // ── Handlers ──
  const handleRestoreClick = (e, item) => {
    setRestoreAnchorEl(e.currentTarget);
    setItemToRestore(item);
  };

  const handleRestoreClose = () => {
    setRestoreAnchorEl(null);
    setItemToRestore(null);
  };

  const handleRestoreConfirm = async () => {
    if (!itemToRestore) return;
    try {
      const res = await restoreBlankLR(itemToRestore.lr_number);
      if (res?.success) {
        toast.success(`LR ${itemToRestore.lr_number} restored successfully!`);
        fetchData();
      } else {
        toast.error(res?.message || "Failed to restore LR");
      }
    } catch {
      toast.error("Failed to restore LR");
    }
    handleRestoreClose();
  };

  const handlePrint = () => {
    const w = window.open("", "_blank");
    w.document.write(`
      <html><head><title>Blank LR List</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; padding: 30px; color: #1e1b4b; }
        h2 { text-align: center; font-size: 18px; margin-bottom: 20px; color: #f59e0b; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th { background: #f59e0b; color: white; padding: 8px 10px; text-align: left; font-weight: 600; }
        td { padding: 7px 10px; border-bottom: 1px solid #fef3c7; }
        tr:nth-child(even) td { background: #fffbeb; }
      </style></head>
      <body><h2>Blank / Void LR Records</h2>
      <table><thead><tr><th>#</th><th>LR No</th><th>Reason</th><th>Marked By</th><th>Marked Date</th></tr></thead>
      <tbody>${filteredData
        .map(
          (u, i) => `
        <tr><td>${i + 1}</td><td><b>${u.lr_number}</b></td><td>${u.reason || "—"}</td>
        <td>${u.marked_by || "—"}</td><td>${formatDateTime(u.marked_at)}</td></tr>
      `
        )
        .join("")}</tbody></table></body></html>
    `);
    w.document.close();
    w.print();
    w.close();
  };

  const handleExportExcel = async () => {
    const XLSX = await import("xlsx-js-style");
    const headers = ["Sr No", "LR No", "Reason", "Marked By", "Marked Date"];
    const rows = filteredData.map((u, i) => [
      i + 1,
      u.lr_number,
      u.reason || "—",
      u.marked_by || "—",
      formatDateTime(u.marked_at),
    ]);

    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10, name: "Segoe UI" },
      fill: { fgColor: { rgb: "F59E0B" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "D97706" } },
        bottom: { style: "thin", color: { rgb: "D97706" } },
        left: { style: "thin", color: { rgb: "D97706" } },
        right: { style: "thin", color: { rgb: "D97706" } },
      },
    };

    const cellStyleEven = {
      font: { sz: 10, name: "Segoe UI", color: { rgb: "1E1B4B" } },
      fill: { fgColor: { rgb: "FFFBEB" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "FEF3C7" } },
        bottom: { style: "thin", color: { rgb: "FEF3C7" } },
        left: { style: "thin", color: { rgb: "FEF3C7" } },
        right: { style: "thin", color: { rgb: "FEF3C7" } },
      },
    };

    const cellStyleOdd = { ...cellStyleEven, fill: { fgColor: { rgb: "FFFFFF" } } };

    const wsData = [
      headers.map((h) => ({ v: h, t: "s", s: headerStyle })),
      ...rows.map((row, rowIdx) =>
        row.map((val) => {
          const isEven = rowIdx % 2 === 0;
          return {
            v: val ?? "—",
            t: typeof val === "number" ? "n" : "s",
            s: isEven ? cellStyleEven : cellStyleOdd,
          };
        })
      ),
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const colWidths = headers.map((_, colIdx) => {
      const allValues = [headers[colIdx], ...rows.map((r) => String(r[colIdx] ?? ""))];
      const maxLen = Math.max(...allValues.map((v) => String(v).length));
      return { wch: Math.min(Math.max(maxLen + 4, 10), 45) };
    });
    ws["!cols"] = colWidths;
    ws["!rows"] = [{ hpt: 18 }, ...rows.map(() => ({ hpt: 18 }))];
    ws["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2" };

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Blank LRs");
    XLSX.writeFile(wb, `BlankLR_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ px: 0, pt: 0, mt: -5, maxWidth: 1400, mx: "auto" }}>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        style={{ top: "70px", right: "16px", zIndex: 99999 }}
      />

      {/* ── Toolbar ── */}
      <Box sx={{ mt: 3, mb: 1.5 }}>
        <Grid container spacing={0.75} alignItems="center">
          {/* Search */}
          <Grid item xs={12} sm={4} md={4}>
            <TextField
              fullWidth
              placeholder="Search by LR number, reason, or user…"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 15, color: "#f59e0b" }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm("")} sx={{ p: 0.25 }}>
                      <CloseIcon sx={{ fontSize: 13, color: T.textSub }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiInputBase-root": {
                  height: 34,
                  ...sq,
                  bgcolor: T.white,
                },
                "& fieldset": { border: `1.5px solid ${T.border}` },
                "& :hover fieldset": { border: `1.5px solid #fbbf24` },
                "& .Mui-focused fieldset": { border: `1.5px solid #f59e0b` },
                "& .Mui-focused": { boxShadow: `0 0 0 3px #fef3c7` },
              }}
            />
          </Grid>

          {/* Reset */}
          <Grid item xs={4} sm={1.5} md={1}>
            <Button fullWidth size="small" onClick={() => setSearchTerm("")} sx={{ ...ghostBtn, height: 34 }}>
              Reset
            </Button>
          </Grid>

          {/* Icon Actions */}
          {[
            { tip: "Print", icon: <Print sx={{ fontSize: 15 }} />, action: handlePrint },
            { tip: "Export Excel", icon: <Download sx={{ fontSize: 15 }} />, action: handleExportExcel },
            { tip: "Refresh", icon: <Refresh sx={{ fontSize: 15 }} />, action: fetchData },
          ].map(({ tip, icon, action }) => (
            <Grid item xs={2} sm={1} md={0.75} key={tip}>
              <Tooltip title={tip} arrow>
                <Button fullWidth size="small" onClick={action} sx={{ ...iconBtn, height: 34 }}>
                  {icon}
                </Button>
              </Tooltip>
            </Grid>
          ))}

          {/* Spacer */}
          <Grid item xs={false} sm={false} md sx={{ display: { xs: "none", md: "block" } }} />

          {/* Stats Badge */}
          <Grid item xs={12} sm={3} md={2.5}>
            <Box
              sx={{
                height: 34,
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                ...sq,
                bgcolor: "#fffbeb",
                color: "#b45309",
                border: `1.5px solid #fbbf24`,
                fontWeight: 700,
                fontSize: "0.72rem",
                boxSizing: "border-box",
              }}
            >
              <BlankIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "#b45309", lineHeight: 1 }}>
                {totalCount} Blank LR{totalCount !== 1 ? "s" : ""}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* ── Table / Cards ── */}
      {isMobile ? (
        loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 10, gap: 2 }}>
            <CircularProgress sx={{ color: "#f59e0b" }} size={32} />
            <Typography sx={{ fontSize: "0.74rem", color: T.textSub }}>Loading…</Typography>
          </Box>
        ) : paginatedData.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              border: `1.5px dashed ${T.border}`,
              bgcolor: T.white,
              ...sq,
            }}
          >
            <BlankIcon sx={{ fontSize: 44, color: T.border, mb: 1 }} />
            <Typography sx={{ fontWeight: 700, color: T.text, mb: 0.5, fontSize: "0.9rem" }}>
              No blank LR records
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", color: T.textSub }}>
              {searchTerm ? "Try a different search term" : "All LR numbers are in active use"}
            </Typography>
          </Box>
        ) : (
          <>
            {paginatedData.map((item) => (
              <MobileBlankCard key={item.blank_id} item={item} onRestore={handleRestoreClick} />
            ))}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              sx={paginationSx}
            />
          </>
        )
      ) : (
        <DesktopBlankTable
          data={paginatedData}
          loading={loading}
          totalCount={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          onRestore={handleRestoreClick}
        />
      )}

      {/* Restore Popover */}
      <RestorePopover
        anchorEl={restoreAnchorEl}
        onClose={handleRestoreClose}
        onConfirm={handleRestoreConfirm}
        lrNumber={itemToRestore?.lr_number}
      />
    </Box>
  );
}